<!--
// nuxt-ui/components/Shared/players/CardDraftPlayer.vue
-->
<script setup lang="ts">
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import type { Image } from "~/src/Shared/Types";
import type { Team } from "~/src/team/domain/models/Team";
import type { Player } from "~/src/player/domain/Player";
import type { UnwrapRefSimple } from "@vue/reactivity";
import nuxtLink from "#app/components/nuxt-link";
import { useImages } from "~/nuxt-ui/Shared/composables/images";
import { usePlayers } from "~/nuxt-ui/Shared/composables/players";
import { slugify } from "~/src/Shared/utils";
import MvpChip from "~/nuxt-ui/components/Shared/players/MvpChip.vue";
import DoubleMvpChip from "~/nuxt-ui/components/Shared/players/DoubleMvpChip.vue";
import InjuredPlayerIcon from "~/nuxt-ui/components/Shared/icons/InjuredPlayerIcon.vue";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

type Props = {
  playerCompleteName: string;
  playerPosition: string | null;
  playerNumber: number | string | null | undefined;
  seasonId: number | undefined;
  playerId: number;
  player?: Player | UnwrapRefSimple<Player>;
  playerImage?: Image;
  playerBudget?: string | null;
  mvpPartner?: SeasonStatsPartner;
  withTeam?: boolean;
  playerTeam?: Team;
  noLink?: boolean;
  loadingLazy?: boolean;
  isTeamSeasonPlayersSlider?: boolean;
  isPlayersDraft?: boolean;
  showClause?: boolean;
};

const props = defineProps<Props>();
const localePath = useLocalePath();
const route = useRoute();
const { playerPlaceholderPath } = useImages();
const { getPlayerSeasonStats } = usePlayers();
const { currentSeason } = useCompetitionStore();

const playerStats = props.seasonId ? await getPlayerSeasonStats(props.seasonId, props.playerId) : [];

const seasonCondition = !$SEASONS_WITHOUT_DATA.includes(Number(props.seasonId));

const playerStatMVP = ref();
const playerStatMVPT = ref();
const playerStatMatchesPlayed = ref();
const playerStatGoals = ref();
const playerStatAssists = ref();
const playerStatYellowCards = ref();
const playerStatRedCards = ref();
const playerStatGoalsConceded = ref();
const playerStatRatio = ref();

if (playerStats) {
  playerStatMVP.value = playerStats.find(r => r.parameter.code === "MVP")?.total;
  playerStatMVPT.value = playerStats.find(r => r.parameter.code === "MVP-T")?.total;
  playerStatMatchesPlayed.value = playerStats.find(r => r.parameter.code === "PG")?.total;
  playerStatGoals.value = playerStats.find(r =>
    seasonCondition ? r.parameter.code === "GOL" : r.parameter.code === "GOL-ALL"
  )?.total;
  playerStatAssists.value = playerStats.find(r => r.parameter.code === "ASS-V")?.total;
  playerStatYellowCards.value = playerStats.find(r => r.parameter.code === "CRT-G")?.total;
  playerStatRedCards.value = playerStats.find(r => r.parameter.code === "CRT-R")?.total;
  playerStatGoalsConceded.value = playerStats.find(r =>
    seasonCondition ? r.parameter.code === "GOL-S" : r.parameter.code === "GOL-ALLS"
  )?.total;
  playerStatRatio.value = playerStats
    .find(r => (seasonCondition ? r.parameter.code === "GOL-S" : r.parameter.code === "GOL-ALLS"))
    ?.totalAVG.toFixed(2);
}

const defaultColor = "#888888";
</script>

<template>
  <div class="player-card-container">
    <component
      :is="noLink ? 'div' : nuxtLink"
      :to="
        localePath({
          name: 'players-playerId-playerName',
          params: {
            playerId: playerId,
            playerName: slugify(playerCompleteName),
          },
        })
      "
      :no-prefetch="noLink ? undefined : true"
      :class="{ 'player-nolink-div': noLink }"
    >
      <div class="h-full relative z-[3]">
        <div
          class="clip-diagonal-right"
          :style="`background-image: linear-gradient(180deg, ${
            playerTeam ? playerTeam.firstColorHEX : defaultColor
          }99 40%, ${playerTeam ? playerTeam.secondColorHEX : defaultColor}99 100%);`"
        ></div>
        <div
          class="clip-diagonal-left"
          :style="`background-image: linear-gradient(180deg, ${
            playerTeam ? playerTeam.firstColorHEX : defaultColor
          }30 0%, ${playerTeam ? playerTeam.secondColorHEX : defaultColor}30 100%);`"
        ></div>
      </div>

      <div class="player-card-content">
        <div class="container-player-img">
          <div
            v-if="
              player?.metaInformation?.injury &&
              player?.metaInformation?.injury === 'true' &&
              seasonId === currentSeason.id
            "
            class="absolute top-1 left-1"
          >
            <injured-player-icon />
          </div>
          <NuxtImg
            :src="playerImage && playerImage.url ? playerImage?.url : playerPlaceholderPath"
            :loading="loadingLazy ? 'lazy' : 'eager'"
            :alt="playerCompleteName"
            height="642"
            width="456"
            sizes="122px sm:128px"
          />
          <div v-if="playerNumber" class="player-number">#{{ playerNumber }}</div>
        </div>
        <div class="w-full min-h-full flex flex-col md:justify-between">
          <div class="container-player-info">
            <div class="container-player-info-text">
              <div>
                <component
                  :is="isTeamSeasonPlayersSlider ? 'p' : isPlayersDraft ? 'h2' : 'h3'"
                  class="player-name"
                >
                  {{ playerCompleteName }}
                </component>

                <p v-if="playerPosition" class="player-role">{{ $t(`${playerPosition}`) }}</p>
                <!--                <div
                  v-if="player?.marketValue && seasonId === currentSeason.id && showClause"
                  class="player-budget"
                >
                  <p>
                    {{ $t("players.card.clause") }}: <span class="font-bold">{{ player.marketValue }} M</span>
                  </p>
                </div>-->
              </div>
              <div v-if="withTeam && playerTeam" class="ml-auto hidden md:block">
                <!-- Mobile -->
                <NuxtImg
                  v-if="playerTeam.logo?.url"
                  :src="playerTeam.logo.url"
                  :alt="playerTeam.completeName || ''"
                  :loading="loadingLazy ? 'lazy' : 'eager'"
                  width="53"
                  height="53"
                  class="team-logo-container"
                />
              </div>
              <div class="flex flex-col-reverse items-end md:flex-row md:items-start">
                <div v-if="withTeam && playerTeam" class="min-w-max md:hidden block">
                  <!-- Desktop -->
                  <NuxtImg
                    v-if="playerTeam.logo?.url"
                    :src="playerTeam.logo.url"
                    :alt="playerTeam.completeName || ''"
                    :loading="loadingLazy ? 'lazy' : 'eager'"
                    class="team-logo-container"
                    width="53"
                    height="53"
                  />
                </div>
                <div class="stats-data-container-total">
                  <p class="stat-description-total">{{ $t("players.card.average.title") }}</p>
                  <p class="stat-value">
                    <span v-if="player?.metaInformation?.average">
                      {{ player.metaInformation.average }}
                    </span>
                    <span v-else> - </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="playerStatMVP || playerStatMVPT"
            class="flex flex-wrap justify-start gap-2 mt-auto md:mb-[0.5rem]"
          >
            <mvp-chip
              v-if="playerStatMVP && !playerStatMVPT"
              :total="playerStatMVP"
              :mvp-partner="mvpPartner"
            ></mvp-chip>
            <mvp-chip
              v-else-if="playerStatMVPT && !playerStatMVP"
              :total="playerStatMVPT"
              is-turn-mvp
              :mvp-partner="mvpPartner"
            ></mvp-chip>
            <double-mvp-chip
              v-else-if="playerStatMVP && playerStatMVPT"
              :mvp-partner="mvpPartner"
              :total-mvp="playerStatMVP"
              :total-mvp-t="playerStatMVPT"
            ></double-mvp-chip>
          </div>

          <div class="container-stats-desktop">
            <div class="stats-data-container">
              <p class="stat-description">{{ $t("players.card.matches.title") }}</p>
              <p class="stat-value">{{ playerStatMatchesPlayed ? playerStatMatchesPlayed : 0 }}</p>
            </div>
            <div class="stats-data-container" v-if="playerPosition === 'goalkeeper'">
              <p class="stat-description">{{ $t("players.card.goalsConceded.title") }}</p>
              <p class="stat-value">{{ playerStatGoalsConceded ? playerStatGoalsConceded : 0 }}</p>
            </div>
            <div class="stats-data-container" v-else>
              <p class="stat-description">{{ $t("players.card.goals.title") }}</p>
              <p class="stat-value">{{ playerStatGoals ? playerStatGoals : 0 }}</p>
            </div>
            <div class="stats-data-container" v-if="playerPosition === 'goalkeeper'">
              <p class="stat-description">{{ $t("players.card.ratio.title") }}</p>
              <p class="stat-value">{{ playerStatRatio ? playerStatRatio : 0 }}</p>
            </div>
            <div class="stats-data-container" v-else>
              <p class="stat-description">{{ $t("players.card.assists.title") }}</p>
              <p class="stat-value">{{ playerStatAssists ? playerStatAssists : 0 }}</p>
            </div>
            <div class="stats-data-container">
              <p class="stat-description">{{ $t("players.card.yellowCards.title") }}</p>
              <p class="stat-value">{{ playerStatYellowCards ? playerStatYellowCards : 0 }}</p>
            </div>
            <div class="stats-data-container">
              <p class="stat-description">{{ $t("players.card.redCards.title") }}</p>
              <p class="stat-value">{{ playerStatRedCards ? playerStatRedCards : 0 }}</p>
            </div>
          </div>
        </div>
        <div class="container-stats-mobile">
          <div class="stats-data-container">
            <p class="stat-description">{{ $t("players.card.matches.title") }}</p>
            <p class="stat-value">{{ playerStatMatchesPlayed ? playerStatMatchesPlayed : 0 }}</p>
          </div>
          <div class="stats-data-container" v-if="playerPosition === 'goalkeeper'">
            <p class="stat-description">{{ $t("players.card.goalsConceded.title") }}</p>
            <p class="stat-value">{{ playerStatGoalsConceded ? playerStatGoalsConceded : 0 }}</p>
          </div>
          <div class="stats-data-container" v-else>
            <p class="stat-description">{{ $t("players.card.goals.title") }}</p>
            <p class="stat-value">{{ playerStatGoals ? playerStatGoals : 0 }}</p>
          </div>
          <div class="stats-data-container" v-if="playerPosition === 'goalkeeper'">
            <p class="stat-description">{{ $t("players.card.ratio.title") }}</p>
            <p class="stat-value">{{ playerStatRatio ? playerStatRatio : 0 }}</p>
          </div>
          <div class="stats-data-container" v-else>
            <p class="stat-description">{{ $t("players.card.assists.title") }}</p>
            <p class="stat-value">{{ playerStatAssists ? playerStatAssists : 0 }}</p>
          </div>
          <div class="stats-data-container">
            <p class="stat-description">{{ $t("players.card.yellowCards.title") }}</p>
            <p class="stat-value">{{ playerStatYellowCards ? playerStatYellowCards : 0 }}</p>
          </div>
          <div class="stats-data-container">
            <p class="stat-description">{{ $t("players.card.redCards.title") }}</p>
            <p class="stat-value">{{ playerStatRedCards ? playerStatRedCards : 0 }}</p>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<style scoped>
.player-card-container {
  @apply md:rounded-lg bg-gray-900 relative h-[285px] md:h-[204px] overflow-hidden;
}

.player-nolink-div {
  @apply w-full h-full;
}

.clip-diagonal-right {
  @apply h-full w-[155px] md:w-[135px] absolute;
  clip-path: polygon(0 0, 100% 0%, 22% 100%, 0% 100%);
}

.clip-diagonal-left {
  @apply w-[65px] h-[158px] md:translate-x-2 bg-opacity-20 absolute bottom-0 right-0;
  clip-path: polygon(100% 0, 100% 0%, 100% 100%, 0% 100%);
}

@media (max-width: 1025px) {
  .clip-diagonal-right,
  .player-card-container:hover div.clip-diagonal-right {
    clip-path: polygon(0 0, 100% 0%, 0% 100%, 0% 100%) !important;
  }
}

.player-card-content {
  @apply grid grid-rows-[180px_1fr] grid-cols-[136px_1fr] gap-y-2 md:gap-y-0 md:flex md:justify-start items-start h-full w-full absolute top-0 right-0 bottom-0 left-0 z-[5] p-4 md:p-3;
}

.container-player-info {
  @apply flex justify-start items-start w-full;
}

.team-logo-container {
  @apply w-[46px] h-[46px] md:h-[53px] md:w-[53px] p-1 object-contain object-center;
}

.player-number {
  @apply text-sm font-bold px-2 py-1 bg-gray-800 rounded-full bg-opacity-60 absolute bottom-1 right-1 z-[3];
}

/*.player-budget {
  @apply text-black text-xs text-center bg-primary rounded max-w-max mt-1 px-1 py-0.5;
}*/

.container-player-img {
  @apply shrink-0 relative rounded bg-[url('/img/bg-card/bg-card-player-image.jpg')] bg-cover bg-center bg-no-repeat max-w-[136px] h-[180px] md:h-full overflow-hidden mr-3.5;
}

.container-player-img::after {
  content: "";
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 51.94%);
  @apply absolute bottom-0 w-full h-20 z-[2];
}

.container-player-img img {
  @apply w-full h-full object-top object-cover;
}

.container-player-info-text {
  @apply flex justify-between items-start min-w-full;
}

.player-name {
  @apply font-bold;
}

.player-role {
  @apply text-gray-400 text-sm leading-[21px] first-letter:capitalize;
}

.clip-diagonal-right,
.player-name,
.player-role {
  @apply transition-all ease-in-out duration-200;
}

.player-card-container:hover div.clip-diagonal-right {
  @apply md:w-[147px];
  clip-path: polygon(0 0, 100% 0%, 29% 100%, 0% 100%);
}

.player-card-container:hover .player-name,
.player-card-container:hover .player-role {
  @apply pl-2;
}

.container-stats-desktop {
  @apply hidden md:flex flex-nowrap gap-1 mt-0;
}

.container-stats-mobile {
  @apply flex md:hidden flex-nowrap gap-1 mt-0 col-span-2;
}

.stats-data-container-total {
  @apply rounded-lg px-1 md:px-2 py-3 bg-primary flex justify-between items-center flex-col min-w-[46px] md:min-w-[66px] !text-black ml-2;
}

.stats-data-container {
  @apply rounded-lg px-1 py-3 bg-gray-800 flex justify-between items-center flex-col basis-full min-h-[65px];
}

.stat-description {
  @apply text-[10px] text-gray-400 text-center;
}

.stat-description-total {
  @apply text-[10px] text-black;
}

.stat-value {
  @apply text-base font-bold;
}
</style>
